import React, { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import PhotoPlane from "@/components/scene/PhotoPlane";
import VolumetricSmoke from "@/components/scene/VolumetricSmoke";

// 44th-floor Sky Lobby — a walkable 3D room. Archival photos are mapped onto the
// side/back walls; white marble pillars line the hall. Free-look + D-pad move.
export default function SkyLobbyScene({ game }) {
  const camera = useThree((s) => s.camera);
  const pillarMat = useMemo(() => new THREE.MeshStandardMaterial({ color: 0xe9e4d6, roughness: 0.5, metalness: 0.05 }), []);
  const pillars = useMemo(() => {
    const a = [];
    for (let z = 2; z > -22; z -= 4) a.push(z);
    return a;
  }, []);

  useFrame(() => {
    const g = game.current;
    const ox = g.camOffX || 0, oz = g.camOffZ || 0;
    const px = ox, py = 1.6, pz = 4 + oz;
    camera.position.set(px, py, pz);
    const yaw = g.lookYaw || 0;
    const pitch = g.lookPitch || 0;
    const cp = Math.cos(pitch);
    camera.lookAt(px + Math.sin(yaw) * cp * 4, py + Math.sin(pitch) * 4, pz - Math.cos(yaw) * cp * 4);
  });

  return (
    <group>
      <fogExp2 attach="fog" args={[0x2a2620, 0.05]} />
      <ambientLight color={0xf0e8d4} intensity={1.05} />
      <hemisphereLight args={[0xfff2d0, 0x2a2a22, 0.6]} />
      {/* ceiling light grid */}
      {pillars.map((z, i) => (
        <group key={`cl${i}`} position={[0, 3.9, z]}>
          <mesh><boxGeometry args={[5, 0.08, 0.8]} /><meshStandardMaterial color={0xfff4d6} emissive={0xffe9a8} emissiveIntensity={1.1} /></mesh>
          <pointLight color={0xffe9c0} intensity={0.7} distance={9} />
        </group>
      ))}

      {/* marble floor + ceiling */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, -8]}>
        <planeGeometry args={[14, 34]} />
        <meshStandardMaterial color={0xb9b2a1} roughness={0.6} metalness={0.1} />
      </mesh>
      <mesh position={[0, 4, -8]} rotation={[Math.PI / 2, 0, 0]}>
        <planeGeometry args={[14, 34]} />
        <meshStandardMaterial color={0xcac3b2} roughness={0.85} side={THREE.DoubleSide} />
      </mesh>

      {/* archival photos mapped onto the 3D side + back walls */}
      <PhotoPlane url="/archival/elevator_bank.png" args={[34, 4]} position={[-6.9, 2, -8]} rotation={[0, Math.PI / 2, 0]} />
      <PhotoPlane url="/archival/lobby_atrium.png" args={[34, 4]} position={[6.9, 2, -8]} rotation={[0, -Math.PI / 2, 0]} />
      <PhotoPlane url="/archival/elevator_hall.png" args={[14, 4]} position={[0, 2, -24.8]} />

      {/* white marble pillars lining the hall */}
      {pillars.map((z, i) => (
        <group key={`p${i}`}>
          <mesh material={pillarMat} position={[-3, 2, z]}><boxGeometry args={[0.6, 4, 0.6]} /></mesh>
          <mesh material={pillarMat} position={[3, 2, z]}><boxGeometry args={[0.6, 4, 0.6]} /></mesh>
        </group>
      ))}

      {/* the wounded man (lying) + kneeling security guard (the 44th-floor beat) */}
      <group position={[-1.4, 0, -3]}>
        <mesh position={[0, 0.2, 0]} rotation={[0, 0, Math.PI / 2]}><capsuleGeometry args={[0.28, 0.9, 4, 8]} /><meshStandardMaterial color={0x7a6f63} roughness={0.9} /></mesh>
        <mesh position={[0.7, 0.28, 0]}><sphereGeometry args={[0.22, 12, 10]} /><meshStandardMaterial color={0xd9b48c} /></mesh>
      </group>
      <group position={[0.6, 0, -3.2]}>
        <mesh position={[0, 0.55, 0]}><capsuleGeometry args={[0.24, 0.5, 4, 8]} /><meshStandardMaterial color={0x2c3a55} roughness={0.85} /></mesh>
        <mesh position={[0, 1.0, 0]}><sphereGeometry args={[0.2, 12, 10]} /><meshStandardMaterial color={0xd9b48c} /></mesh>
      </group>

      <VolumetricSmoke count={14} area={[10, 3.5, 20]} center={[0, 2.4, -10]} color={0x2a2620} opacity={0.4} rise={0.18} getIntensity={() => 1} clearPoint={[0, 1.2, -3]} clearRadius={3.4} />
    </group>
  );
}
